import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
  EventStatistics,
  EventStatisticsProgress,
  OdysseyEventStatistics,
  OdysseyRead,
  RobotRead,
  SchedulerSettings,
} from 'api/schema'
import { addSeconds } from 'date-fns'
import { getTimezoneOffset as getIanaTimezoneOffset } from 'date-fns-tz'
import { t } from 'i18next'
import React from 'react'
import { PATH } from 'utils/constants'
import { hasRecurringWeekdays, isRescheduleDescription } from 'utils/odysseyReschedule'
import {
  COMMON_TIMEZONE_OPTIONS,
  COMMON_TIMEZONES,
  formatTimezoneLabel,
} from 'utils/timezones'
import { VENDOR } from 'utils/vendorDisplayName'
import type { VendorCode } from 'utils/vendorDisplayName'

import schedulerStyle from './SchedulerSettings.module.css'

// Re-export shared timezone helpers for existing TaskScheduler imports.
export { COMMON_TIMEZONE_OPTIONS, COMMON_TIMEZONES, formatTimezoneLabel }

// --- Timezone display helpers ---
export const formatUtcOffset = (offsetMinutes: number): string => {
  const sign = offsetMinutes >= 0 ? '+' : '-'
  const abs = Math.abs(offsetMinutes)
  const h = Math.floor(abs / 60)
  const m = abs % 60
  return `UTC${sign}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

export const UTC_OFFSET_OPTIONS = Array.from({ length: 27 }, (_, i) => {
  const minutes = (i - 12) * 60
  return { value: minutes, text: formatUtcOffset(minutes) }
})

export interface ProcessedEvent {
  event_id: string | number
  title: string | React.ReactNode
  start: Date
  end: Date
  color?: string
  description?: string | React.ReactNode
  editable?: boolean
  deletable?: boolean
  draggable?: boolean
  allDay?: boolean
  duration?: number
  extendedProps?: any
}

/**
 * Generates a list of ProcessedEvents to occur within the query start and end,
 * based on the provided schedule.
 * @param start The start of the query, which is generally 00:00:00 of the first
 * day in the calendar view.
 * @param end The end of the query, which is generally 23:59:59 of the last day
 * in the calendar view.
 * @param task The current scheduled task, to be checked if there are any events
 * between start and end.
 * @param description Task description.
 * @param robot_name Robot name.
 * @returns List of ProcessedEvents to occur within the query start and end.
 */
export const scheduleToEvents = (
  start: Date,
  end: Date,
  task: OdysseyRead,
  getEventColor: () => string | undefined,
  description: string | React.ReactNode,
  robot: RobotRead,
  missionMap: Map<number, string>,
  facilityIds: number[] = [],
  displayOffsetMinutes?: number
): ProcessedEvent[] => {
  if (!task) {
    console.warn('Task is null!')
    return []
  }
  const robot_name = robot.name || robot.serial_number || ''
  const robot_sn = robot.serial_number
  const robot_type = (robot as any).robot_type_display || robot.robot_type
  const vendor = (robot.vendor as VendorCode) || VENDOR.AUTONOMICS
  const task_type = 'Task'
  const displayedTaskType = 'Task'
  const id_prefix = 'odyssey_'
  const mission_name = task.name
  let eventDate: Date
  let targetHours: number | undefined
  let targetMinutes: number | undefined
  let taskOffsetMinutes: number | undefined
  const placeWallClock = (date: Date, hours: number, minutes: number): Date =>
    setWallClockOnDate(
      date,
      hours,
      minutes,
      taskOffsetMinutes ?? resolveOffsetMinutesForDate(date, displayOffsetMinutes)
    )

  if (task.scheduler_settings && task.scheduler_settings.datetime) {
    const isoStr = task.scheduler_settings.datetime.replace(' ', 'T')
    const rawDate = new Date(isoStr)
    if (isNaN(rawDate.getTime())) return []
    eventDate = rawDate
    const wallClock = parseWallClockFromDatetime(task.scheduler_settings.datetime)
    if (wallClock) {
      targetHours = wallClock.hours
      targetMinutes = wallClock.minutes
    }
    let offset = parseOffsetMinutesFromDatetime(task.scheduler_settings.datetime)
    if (offset === null && task.scheduler_settings.timezone) {
      try {
        offset = getIanaTimezoneOffset(task.scheduler_settings.timezone, rawDate) / 60000
      } catch {
        // fallback to undefined
      }
    }
    if (offset !== null) {
      taskOffsetMinutes = offset
    }
  } else {
    eventDate = new Date(task.created_at.replace(' ', 'T'))
    const validDateString = task.created_at.slice(0, 16) // Remove extra info
    eventDate = new Date(validDateString)
  }

  // Fallback to local hours if no string match (unlikely for datetime)
  if (targetHours === undefined) targetHours = eventDate.getHours()
  if (targetMinutes === undefined) targetMinutes = eventDate.getMinutes()

  if ((!hasRecurringWeekdays(task.scheduler_settings?.weekdays) && eventDate < start) || eventDate > end) {
    return []
  }
  const events: ProcessedEvent[] = []

  // Prefer Duration P90 (predicted_duration); then CRC; then 3h.
  let duration = 3 * 3600
  if (task.predicted_duration) {
    duration = task.predicted_duration
  } else if (task.execution_time) {
    duration = task.execution_time
  }

  const dayDuration = 24 * 60 * 60 * 1000
  const weekDuration = 7 * dayDuration
  const taskName = task?.name || `Task ${task.id}`
  const settings = task.scheduler_settings
  const weekdays = settings?.weekdays
  if (settings && hasRecurringWeekdays(weekdays)) {
    let dayToStart = new Date(Math.max(start.getTime(), eventDate.getTime()))
    const daysOfWeek = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as (keyof NonNullable<
      SchedulerSettings['weekdays']
    >)[]
    let counter = 0
    for (const dayOfWeek of daysOfWeek) {
      if (weekdays[dayOfWeek] === 'true') {
        let dayOffset = daysOfWeek.indexOf(dayOfWeek) - dayToStart.getDay()
        // Ensure dayOffset is always positive
        if (dayOffset < 0) {
          dayOffset += 7
        }
        let startDate = new Date(dayToStart.valueOf() + dayOffset * dayDuration)
        startDate = placeWallClock(startDate, targetHours, targetMinutes)

        const displayedDescription = React.createElement(
          React.Fragment,
          {},
          React.createElement(
            'div',
            {},
            React.createElement('span', { style: { fontWeight: 'bold' } }, displayedTaskType + ': '),
            task.id
          ),
          React.createElement(
            'div',
            {},
            React.createElement('span', { style: { fontWeight: 'bold' } }, 'Robot: '),
            React.createElement(
              'a',
              { className: schedulerStyle.link, href: PATH.ROBOT + robot.id, target: '_blank', rel: 'noreferrer' },
              robot_name || (robot as any).sn || (robot as any).serial_number
            )
          ),
          description
        )
        const { events: newEvents, counter: nextCounter } = addEvents(
          startDate,
          end,
          weekDuration,
          id_prefix + task.id,
          taskName,
          getEventColor,
          duration,
          settings.except_days,
          displayedDescription,
          counter,
          {
            taskOffsetMinutes,
            task_type,
            task_id: task.id,
            missions: 'missions' in task ? task.missions : undefined,
            missions_info: 'missions_info' in task ? task.missions_info : undefined,
            scenario: 'scenario' in task ? task.scenario : undefined,
            robot_type,
            robot_name,
            robot_sn,
            robot_id: robot.id,
            robot_color: robot.robot_color,
            mission_name: mission_name || '',
            vendor: vendor,
            facility_ids: facilityIds,
          },
          settings.except_days_descriptions,
          settings.except_days_users,
          targetHours,
          targetMinutes,
          displayOffsetMinutes,
          taskOffsetMinutes
        )
        events.push(...newEvents)
        counter = nextCounter
      }
    }
  } else if (eventDate >= start && eventDate <= end) {
    if (targetHours !== undefined && targetMinutes !== undefined) {
      eventDate = placeWallClock(eventDate, targetHours, targetMinutes)
    }

    const displayedDescription = React.createElement(
      React.Fragment,
      {},
      React.createElement(
        'div',
        {},
        React.createElement('span', { style: { fontWeight: 'bold' } }, displayedTaskType + ': '),
        task.id
      ),
      React.createElement(
        'div',
        {},
        React.createElement('span', { style: { fontWeight: 'bold' } }, 'Robot: '),
        React.createElement(
          'a',
          { className: schedulerStyle.link, href: PATH.ROBOT + robot.id, target: '_blank', rel: 'noreferrer' },
          robot_name || (robot as any).sn || (robot as any).serial_number
        )
      ),
      description
    )
    const newEvent: ProcessedEvent = {
      start: eventDate,
      end: addSeconds(eventDate, duration),
      event_id: id_prefix + task.id + '_0',
      title: taskName,
      color: getEventColor(),
      duration: duration,
      description: displayedDescription,
    }
    // @ts-ignore
    newEvent.extendedProps = {
      taskOffsetMinutes,
      task_type,
      task_id: task.id,
      missions: 'missions' in task ? task.missions : undefined,
      missions_info: 'missions_info' in task ? task.missions_info : undefined,
      scenario: 'scenario' in task ? task.scenario : undefined,
      base_description: description,
      robot_type,
      robot_name,
      robot_sn,
      robot_id: robot.id,
      robot_color: robot.robot_color,
      mission_name: mission_name || '',
      vendor: vendor,
      facility_ids: facilityIds,
    }
    events.push(newEvent)
  }

  return events
}

export const addEvents = (
  start: Date,
  end: Date,
  period: number,
  id: string,
  title: string,
  getEventColor: () => string | undefined,
  duration: number,
  except_days: string[] = [],
  description: string | React.ReactNode,
  counter: number,
  extendedProps?: any,
  except_days_descriptions?: Record<string, string> | null,
  except_days_users?: Record<string, string> | null,
  targetHours?: number,
  targetMinutes?: number,
  displayOffsetMinutes?: number,
  taskOffsetMinutes?: number
): { events: ProcessedEvent[]; counter: number } => {
  let events: ProcessedEvent[] = []
  const today = new Date()
  while (start <= end) {
    if (targetHours !== undefined && targetMinutes !== undefined) {
      start = setWallClockOnDate(
        start,
        targetHours,
        targetMinutes,
        taskOffsetMinutes ?? resolveOffsetMinutesForDate(start, displayOffsetMinutes)
      )
    }

    const dateStr = toISOStringYYYYMMDD(start)
    if (except_days.includes(dateStr)) {
      // Reschedule: original series slot is skipped; the one-shot is drawn at the new time.
      // User cancel: keep a gray "deleted" card when a real cancel reason is stored.
      if (
        except_days_descriptions &&
        except_days_descriptions[dateStr] &&
        !isRescheduleDescription(except_days_descriptions[dateStr])
      ) {
        const desc = except_days_descriptions[dateStr]
        const user = except_days_users?.[dateStr]
        events.push({
          start: start,
          end: addSeconds(start, duration),
          event_id: id + '_' + counter,
          title: title,
          color: 'gray',
          extendedProps: {
            ...extendedProps,
            status: 'deleted',
            exception_reason: desc,
            exception_user: user,
          },
          description: description,
          editable: false,
          deletable: false,
        })
        counter++
      }
      start = new Date(start.valueOf() + period)
      continue
    }

    let displayedDescription = description
    let editable = true
    if (start < today) {
      editable = false
    }
    events.push({
      start: start,
      end: addSeconds(start, duration),
      event_id: id + '_' + counter,
      title: title,
      color: getEventColor(),
      extendedProps: {
        ...extendedProps,
        status: 'scheduled',
        base_description: description,
      },
      description: displayedDescription,
      editable: editable,
      deletable: editable,
    })
    start = new Date(start.valueOf() + period)
    counter++
  }

  return { events, counter }
}

export const renderStatsTable = (eventProgerss: EventStatisticsProgress) => {
  if (eventProgerss) {
    const rows: { label: string; value: string }[] = []

    const efficiency =
      eventProgerss.cleaning_performance > 0.01
        ? eventProgerss.cleaning_performance
        : (eventProgerss.cleaned_area_actual / eventProgerss.duration_overall) * 3600
    if (efficiency) {
      rows.push({ label: '• Cleaning performance:', value: `${efficiency.toFixed(1)} m\u00B2/h` })
    }

    // Progress: prefer explicit completion_percentage (GS), fallback to travel_distance ratio (Autonomics).
    const completionRaw = (eventProgerss as any).completion_percentage
    if (completionRaw != null && Number.isFinite(Number(completionRaw))) {
      let pct = Number(completionRaw)
      // GS reports may store 0..1; normalize to percent.
      if (pct >= 0 && pct <= 1) pct = pct * 100
      // Clamp and display if meaningful.
      pct = Math.max(0, Math.min(100, pct))
      if (pct > 0) rows.push({ label: '• Progress:', value: `${pct.toFixed(1)}%` })
    } else {
      let mission_progress = (eventProgerss.travel_distance_actual / eventProgerss.travel_distance_planned) * 100.0
      mission_progress = mission_progress > 99.0 ? 100.0 : mission_progress
      if (mission_progress) {
        rows.push({ label: '• Progress:', value: `${mission_progress.toFixed(1)}%` })
      }
    }

    if (eventProgerss.cleaned_area_planned) {
      rows.push({ label: '• Cleaning area:', value: `${eventProgerss.cleaned_area_planned.toFixed(1)} m\u00B2` })
    }

    if (eventProgerss.duration_overall) {
      rows.push({ label: '• Duration:', value: secToTime(eventProgerss.duration_overall) })
    }

    if ((eventProgerss as any).battery_begin != null && (eventProgerss as any).battery_end != null) {
      const begin = Number((eventProgerss as any).battery_begin)
      const end = Number((eventProgerss as any).battery_end)
      const diff = begin - end
      if (Number.isFinite(diff) && diff !== 0) {
        if (diff > 0) rows.push({ label: '• Battery charge used:', value: `${diff.toFixed(1)}%` })
        else rows.push({ label: '• Battery charged:', value: `${Math.abs(diff).toFixed(1)}%` })
      }
    }

    if (eventProgerss.water_level_begin != null && eventProgerss.water_level_end != null) {
      const value = eventProgerss.water_level_begin - eventProgerss.water_level_end
      if (value > 0) rows.push({ label: '• Clean water used:', value: `${value.toFixed(1)}%` })
    } else if ((eventProgerss as any).water_consumption != null) {
      const value = Number((eventProgerss as any).water_consumption)
      if (Number.isFinite(value) && value > 0) {
        const unit = String((eventProgerss as any).water_consumption_unit || '%')
        rows.push({
          label: '• Clean water used:',
          value: `${value.toFixed(1)}${unit === 'percent' ? '%' : ` ${unit}`}`,
        })
      }
    }

    // Dirty water filled (normalized sources provide dirty_water_consumption, Autonomics progress may have levels).
    if ((eventProgerss as any).dirty_water_consumption != null) {
      const value = Number((eventProgerss as any).dirty_water_consumption)
      if (Number.isFinite(value) && value > 0) {
        const unit = String((eventProgerss as any).dirty_water_consumption_unit || '%')
        rows.push({
          label: '• Dirty water filled:',
          value: `${value.toFixed(1)}${unit === 'percent' ? '%' : ` ${unit}`}`,
        })
      }
    } else if (
      (eventProgerss as any).dirty_water_level_begin != null &&
      (eventProgerss as any).dirty_water_level_end != null
    ) {
      const begin = Number((eventProgerss as any).dirty_water_level_begin)
      const end = Number((eventProgerss as any).dirty_water_level_end)
      const value = end - begin
      if (Number.isFinite(value) && value > 0)
        rows.push({ label: '• Dirty water filled:', value: `${value.toFixed(1)}%` })
    }

    // Garbage filled (Autonomics progress uses garbage_filled_begin/end; normalized also has them).
    if ((eventProgerss as any).garbage_filled_begin != null && (eventProgerss as any).garbage_filled_end != null) {
      const begin = Number((eventProgerss as any).garbage_filled_begin)
      const end = Number((eventProgerss as any).garbage_filled_end)
      const value = end - begin
      if (Number.isFinite(value) && value > 0) rows.push({ label: '• Garbage filled:', value: `${value.toFixed(1)}%` })
    }

    if (rows.length === 0) return null

    return React.createElement(
      'table',
      { style: { width: 'auto', fontSize: '0.875rem', borderCollapse: 'collapse' } },
      React.createElement(
        'tbody',
        {},
        rows.map((row, index) =>
          React.createElement(
            'tr',
            { key: index },
            React.createElement(
              'td',
              { style: { paddingRight: '10px', verticalAlign: 'top', whiteSpace: 'nowrap' } },
              row.label
            ),
            React.createElement('td', { style: { verticalAlign: 'top' } }, row.value)
          )
        )
      )
    )
  }
  return null
}

const isTaskStat = (s: EventStatistics | OdysseyEventStatistics): s is EventStatistics => {
  return 'task_id' in s
}

export const getTaskStatusIcon = (status: number | null | undefined, isStale: boolean = false): React.ReactNode => {
  if (status != null && Number.isFinite(Number(status))) {
    const n = Number(status)
    switch (n) {
      case 4:
        return React.createElement(TaskAltIcon, { style: { fontSize: '1.3em' }, color: 'inherit' })
      case 7:
        return React.createElement(StopCircleOutlinedIcon, { style: { fontSize: '1.3em' }, color: 'inherit' })
      case 8:
        return React.createElement(WarningAmberIcon, { style: { fontSize: '1.3em' }, color: 'inherit' })
      case 9:
        return React.createElement(ErrorOutlineIcon, { style: { fontSize: '1.3em' }, color: 'inherit' })
      case 10:
        return React.createElement(BlockOutlinedIcon, { style: { fontSize: '1.3em' }, color: 'inherit' })
      case 255:
        return React.createElement(HelpOutlineIcon, { style: { fontSize: '1.3em' }, color: 'inherit' })
      case 99:
        return null
    }
  }

  if (isStale) {
    return React.createElement(HelpOutlineIcon, { style: { fontSize: '1.3em' }, color: 'inherit' })
  }

  return React.createElement(HourglassEmptyIcon, { style: { fontSize: '1.3em' }, color: 'inherit' })
}

export const getCleanEventTitle = (title: string | React.ReactNode): string => {
  let titleStr = ''
  if (typeof title === 'string') {
    titleStr = title
  } else if (React.isValidElement(title)) {
    const children = (title.props as any)?.children
    if (Array.isArray(children)) {
      const lastChild = children[children.length - 1]
      if (typeof lastChild === 'string') titleStr = lastChild
    }
  }
  return titleStr.replace(/^[❌✔？⛔⚠️⏳]\s/, '').trim()
}

export const applyStatToEvent = (event: ProcessedEvent, stat: EventStatistics | OdysseyEventStatistics) => {
  const isTask = isTaskStat(stat)
  const status = isTask ? (stat as EventStatistics).mission_status : (stat as OdysseyEventStatistics).status
  const isStale = event.start && Date.now() - event.start.getTime() > 24 * 60 * 60 * 1000
  const isStarted = event.start && event.start.getTime() <= Date.now()
  let icon = getTaskStatusIcon(status, isStale)

  if (!status && !isStale && !isStarted) {
    icon = null
  }

  event.extendedProps = {
    ...event.extendedProps,
    statusPrefix: icon,
  }

  if (isTaskStat(stat)) {
    event.extendedProps = {
      ...event.extendedProps,
      status: 'executed',
      mission_status: status,
      mission_progress: stat.mission_progress,
    }
    const statsTable = renderStatsTable(stat.mission_progress)
    const reportDescription = statsTable
      ? React.createElement(
          'div',
          { style: { marginTop: '4px' } },
          React.createElement(
            'div',
            {},
            React.createElement('div', { style: { fontWeight: 'bold' } }, 'Report:'),
            statsTable
          )
        )
      : null
    const baseDescription = event.extendedProps?.base_description ?? event.description
    if (baseDescription && reportDescription) {
      event.description = React.createElement(React.Fragment, {}, baseDescription, reportDescription)
    } else {
      event.description = baseDescription || reportDescription || ''
    }
    event.start = new Date(stat.created_at.replace(' ', 'T'))
    event.end = addSeconds(
      event.start,
      stat.mission_progress.duration_overall > 0.1 ? stat.mission_progress.duration_overall : 10
    )
  } else {
    event.extendedProps = {
      ...event.extendedProps,
      status: 'executed',
      overall_progress: (stat as OdysseyEventStatistics).overall_progress,
      missions_progress: (stat as OdysseyEventStatistics).missions_progress,
    }
    event.start = new Date((stat as OdysseyEventStatistics).created_at.replace(' ', 'T'))
    event.end = addSeconds(
      event.start,
      (stat as OdysseyEventStatistics).overall_progress.duration_overall > 0.1
        ? (stat as OdysseyEventStatistics).overall_progress.duration_overall
        : 10
    )
  }
}

export const renderMissionsWithNames = (missions: { id: number; name: string }[]) => {
  if (missions && missions.length === 1) {
    const m = missions[0]
    return React.createElement(
      'div',
      { style: { marginTop: '0px', fontSize: '0.875rem' } },
      React.createElement('span', { style: { fontWeight: 'bold' } }, 'Mission: '),
      React.createElement(
        'a',
        {
          className: schedulerStyle.link,
          href: `${PATH.MISSION}${m.id}`,
          target: '_blank',
          rel: 'noreferrer',
        },
        m.id.toString()
      ),
      ` ${m.name}`
    )
  }

  if (missions && missions.length > 1) {
    const label = React.createElement('div', { style: { marginBottom: '0px', fontWeight: 'bold' } }, 'Missions:')

    const tableHeader = React.createElement(
      'thead',
      {},
      React.createElement(
        'tr',
        {},
        React.createElement('th', { style: { textAlign: 'left', paddingRight: '10px' } }, '#'),
        React.createElement('th', { style: { textAlign: 'left', paddingRight: '10px' } }, 'ID'),
        React.createElement('th', { style: { textAlign: 'left' } }, 'Name')
      )
    )

    const tableBody = React.createElement(
      'tbody',
      {},
      missions.map((m, index) =>
        React.createElement(
          'tr',
          { key: `${m.id}_${index}` },
          React.createElement('td', { style: { paddingRight: '10px' } }, `${index + 1}`),
          React.createElement(
            'td',
            { style: { paddingRight: '10px' } },
            React.createElement(
              'a',
              {
                className: schedulerStyle.link,
                href: `${PATH.MISSION}${m.id}`,
                target: '_blank',
                rel: 'noreferrer',
              },
              m.id.toString()
            )
          ),
          React.createElement('td', {}, m.name)
        )
      )
    )

    return React.createElement(
      'div',
      { style: { marginTop: '0px', fontSize: '0.875rem' } },
      label,
      React.createElement('table', { style: { width: 'auto' } }, tableHeader, tableBody)
    )
  }
  return null
}

export const renderMissionName = (name: string) => {
  return React.createElement(
    'div',
    { style: { marginBottom: '0px' } },
    React.createElement('span', { style: { fontWeight: 'bold' } }, 'Mission name:'),
    ` ${name}`
  )
}

export const renderScenario = (scenario: number) => {
  return React.createElement(
    'div',
    { style: { marginBottom: '2px' } },
    React.createElement('span', { style: { fontWeight: 'bold' } }, 'Scenario: '),
    React.createElement(
      'a',
      {
        className: schedulerStyle.link,
        href: `${PATH.SCENARIOS}${scenario}`,
        target: '_blank',
        rel: 'noreferrer',
      },
      scenario.toString()
    )
  )
}

export const renderMissions = (missions: (number | { id: number; name?: string })[]) => {
  const validMissions = missions.filter(m => m != null && m !== 0)
  if (validMissions.length > 0) {
    const children: React.ReactNode[] = []
    children.push(
      React.createElement(
        'span',
        { style: { fontWeight: 'bold' } },
        validMissions.length > 1 ? 'Missions: ' : 'Mission: '
      )
    )
    validMissions.forEach((m: any, index: number) => {
      const id = typeof m === 'object' ? m.id : m
      const name = typeof m === 'object' ? m.name ?? m.id.toString() : m.toString()

      children.push(
        React.createElement(
          React.Fragment,
          { key: `${id}_${index}` },
          React.createElement(
            'a',
            {
              className: schedulerStyle.link,
              href: `${PATH.MISSION}${id}`,
              target: '_blank',
              rel: 'noreferrer',
            },
            name
          ),
          index < validMissions.length - 1 ? ', ' : ''
        )
      )
    })
    children.push(React.createElement('br', {}))
    return React.createElement(
      'div',
      { className: schedulerStyle.missionsList, style: { marginTop: '0px' } },
      ...children
    )
  }
  return null
}

export const addEventsStatistics = (
  events: ProcessedEvent[],
  events_statistics: (EventStatistics | OdysseyEventStatistics)[],
  robots: RobotRead[],
  missionMap: Map<number, string>,
  robotIdToFacilityIds: Map<number, number[]>
) => {
  // Create a map of robot serial numbers to robot objects for efficient lookup
  const robotMap = new Map<string, RobotRead>()
  robots.forEach(robot => {
    robotMap.set(robot.serial_number, robot)
  })

  events_statistics.forEach(stat => {
    const isTask = isTaskStat(stat)
    const id = isTask ? stat.task_id : (stat as OdysseyEventStatistics).odyssey_id
    const missions = isTask
      ? [stat.mission_id]
      : (stat as OdysseyEventStatistics).overall_progress.missions
      ? (stat as OdysseyEventStatistics).overall_progress.missions
      : (stat as OdysseyEventStatistics).missions_progress.map(m => m.id)

    const prefix = isTask ? 'task_' : 'odyssey_'
    const eventIdStr = prefix + id
    const statDateStr = toISOStringYYYYMMDD(new Date(stat.created_at))

    const eventIndex = events.findIndex(e => {
      const eId = String(e.event_id)
      const parts = eId.split('_')
      if (parts[0] !== (isTask ? 'task' : 'odyssey')) return false
      if (Number(parts[1]) !== id) return false
      if (toISOStringYYYYMMDD(e.start) !== statDateStr) return false
      // extra check for events with icons (already processed)
      const title = e.title
      const isProcessed =
        React.isValidElement(title) ||
        (typeof title === 'string' && ['✔', '？', '⛔', '⚠️', '⏳'].some(icon => title.startsWith(icon + ' ')))
      return !isProcessed
    })

    if (eventIndex !== -1) {
      events[eventIndex].editable = false
      events[eventIndex].deletable = false
      events[eventIndex].draggable = false
      applyStatToEvent(events[eventIndex], stat)
    } else {
      const duration = isTask
        ? (stat as EventStatistics).mission_progress.duration_overall
        : (stat as OdysseyEventStatistics).overall_progress.duration_overall
      const task_name =
        isTask || (stat as OdysseyEventStatistics).overall_progress.name === undefined
          ? ''
          : (stat as OdysseyEventStatistics).overall_progress.name
      const taskName = task_name || `Task ${id}`
      const robot = robotMap.get((stat as any).robot_sn.toString())
      const robotIdEffective = robot?.id ?? (stat as any).robot_sn
      const robotNameEffective = robot?.name || (robot as any)?.displayName || (stat as any).robot_sn
      const robotLink = React.createElement(
        'a',
        { className: schedulerStyle.link, href: PATH.ROBOT + robotIdEffective, target: '_blank', rel: 'noreferrer' },
        robotNameEffective
      )
      const description = React.createElement(
        React.Fragment,
        {},
        React.createElement('div', {}, React.createElement('span', { style: { fontWeight: 'bold' } }, 'Task: '), id),
        React.createElement(
          'div',
          {},
          React.createElement('span', { style: { fontWeight: 'bold' } }, 'Robot: '),
          robotLink
        ),
        (stat as any).scenario_id ? renderScenario((stat as any).scenario_id) : null,
        (stat as any).status === 99
          ? React.createElement(
              React.Fragment,
              {},
              t('scheduler.no_statistic') + '\n',
              renderMissionsWithNames((stat as OdysseyEventStatistics).missions_info)
            )
          : ''
      )
      const robotTypeEffective = robot != null ? (robot as any).robot_type_display || robot.robot_type : undefined
      const facilityIds = robot?.id != null ? robotIdToFacilityIds.get(robot.id) ?? [] : []
      const vendor = ((robot as any)?.vendor as VendorCode | undefined) || VENDOR.AUTONOMICS
      const newEvent: ProcessedEvent = {
        event_id: eventIdStr + '_stat_' + stat.id,
        title: taskName,
        start: new Date((stat as any).created_at.replace(' ', 'T')),
        end: addSeconds(new Date((stat as any).created_at.replace(' ', 'T')), duration > 0.1 ? duration : 10),
        color: getColorByColorName(robot?.robot_color || 'White'),
        description: description,
        extendedProps: {
          task_type: isTask ? 'Task' : 'Odyssey',
          task_id: id,
          missions: missions,
          missions_info: (stat as OdysseyEventStatistics).missions_info || [],
          robot_type: robotTypeEffective ?? robot?.robot_type,
          robot_name: robot?.name,
          robot_sn: (stat as any).robot_sn,
          robot_id: robot?.id,
          mission_name: isTask
            ? (stat as EventStatistics).mission_progress.name
            : (stat as OdysseyEventStatistics).overall_progress.name,
          vendor: vendor,
          facility_ids: facilityIds,
        },
        editable: false,
        deletable: false,
        draggable: false,
      }

      applyStatToEvent(newEvent, stat)
      events.push(newEvent)
    }
  })
}

const getEventStats = (
  task_id: number,
  date: Date,
  stats: (EventStatistics | OdysseyEventStatistics)[]
): EventStatistics | OdysseyEventStatistics | null => {
  const eventStats = stats.find(stat => {
    const createdMatch = toISOStringYYYYMMDD(new Date(stat.created_at)) === toISOStringYYYYMMDD(date)
    if (isTaskStat(stat)) {
      return stat.task_id === task_id && createdMatch
    } else {
      return (stat as OdysseyEventStatistics).odyssey_id === task_id && createdMatch
    }
  })

  if (eventStats) {
    return eventStats
  }
  return null
}

const idToColorMap: Record<number, string> = {
  1: 'BlueViolet',
  2: 'Crimson',
  3: 'DarkCyan',
  4: 'DodgerBlue',
  5: 'ForestGreen',
  6: 'Gold',
  7: 'Indigo',
  8: 'LimeGreen',
  9: 'MediumOrchid',
  10: 'MediumPurple',
  11: 'MediumSeaGreen',
  12: 'NavajoWhite',
  13: 'OliveDrab',
  14: 'OrangeRed',
  15: 'Orchid',
  16: 'PaleTurquoise',
  17: 'RoyalBlue',
  18: 'SlateBlue',
  19: 'SpringGreen',
  20: 'Tomato',
  21: 'Turquoise',
  22: 'Violet',
  23: 'Wheat',
  24: 'YellowGreen',
  25: 'Aqua',
  26: 'Aquamarine',
  27: 'CornflowerBlue',
  28: 'HotPink',
  29: 'Chartreuse',
  30: 'Chocolate',
  31: 'CadetBlue',
  32: 'DarkGoldenRod',
  33: 'DarkKhaki',
  34: 'DarkMagenta',
  35: 'DarkOliveGreen',
  36: 'DarkOrange',
  37: 'DarkOrchid',
  38: 'DarkRed',
  39: 'DarkSalmon',
  40: 'DarkSlateBlue',
  41: 'DeepPink',
  42: 'DeepSkyBlue',
  43: 'DimGray',
  44: 'FireBrick',
  45: 'Gainsboro',
  46: 'GhostWhite',
  47: 'GoldenRod',
  48: 'GreenYellow',
  49: 'BurlyWood',
  50: 'IndianRed',
  51: 'Ivory',
  52: 'Khaki',
  53: 'Lavender',
  54: 'LavenderBlush',
  55: 'LawnGreen',
  56: 'LightCoral',
  57: 'LightCyan',
  58: 'LightGoldenRodYellow',
  59: 'LightPink',
  60: 'LightSalmon',
}
const nameToColorMap: Record<string, string> = {
  White: 'DarkGrey',
  Black: 'Black',
  Pink: 'HotPink',
  Red: 'FireBrick',
  Blue: 'CornflowerBlue',
  Green: 'Green',
  Yellow: 'Gold',
  Orange: 'DarkOrange',
  Purple: 'RebeccaPurple',
}

export const getColorById = (id: number): string => {
  return idToColorMap[id] || 'Gray' // Default color
}

export const getColorByColorName = (color: string): string => {
  return nameToColorMap[color] || 'Gray' // Default color
}

export function secToTime(sec: number): string {
  const totalSeconds = Math.max(0, Number(sec) || 0)
  const totalHours = Math.floor(totalSeconds / 3600)
  const days = Math.floor(totalHours / 24)
  const hours = totalHours % 24
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = Math.round(totalSeconds % 60)

  const parts: string[] = []
  if (days > 0) parts.push(days + 'd')
  if (hours > 0 || days > 0) parts.push(hours + 'h')
  if (minutes > 0 || hours > 0 || days > 0) parts.push(minutes + 'm')
  parts.push(seconds + 's')
  return parts.join(' ')
}

// Pad a number to 2 digits
function pad(n: number): string {
  return `${Math.floor(Math.abs(n))}`.padStart(2, '0')
}

/** UTC offset in minutes from stored scheduler datetime suffix (+hh:mm / -hh:mm). */
export function parseOffsetMinutesFromDatetime(datetime: string): number | null {
  const iso = datetime.trim().replace(' ', 'T')
  const match = iso.match(/([+-])(\d{2}):(\d{2})$/)
  if (!match) return null
  const sign = match[1] === '+' ? 1 : -1
  return sign * (Number(match[2]) * 60 + Number(match[3]))
}

/** Wall-clock HH:MM from stored scheduler datetime (ignores current browser offset). */
export function parseWallClockFromDatetime(datetime: string): { hours: number; minutes: number } | null {
  const iso = datetime.trim().replace(' ', 'T')
  const match = iso.match(/T(\d{2}):(\d{2})(?::(\d{2}))?/)
  if (!match) return null
  return { hours: Number(match[1]), minutes: Number(match[2]) }
}

/** Shift UTC instant so browser-local Date getters match wall-clock at `offsetMinutes` (for pickers). */
export function shiftDateForOffsetPicker(date: Date, offsetMinutes: number): Date {
  const browserOffsetMinutes = -date.getTimezoneOffset()
  const shiftMs = (offsetMinutes - browserOffsetMinutes) * 60_000
  return shiftMs !== 0 ? new Date(date.getTime() + shiftMs) : date
}

/** Reverse {@link shiftDateForOffsetPicker} — picker value back to UTC instant. */
export function unshiftDateFromOffsetPicker(pickerDate: Date, offsetMinutes: number): Date {
  const browserOffsetMinutes = -pickerDate.getTimezoneOffset()
  const shiftMs = (browserOffsetMinutes - offsetMinutes) * 60_000
  return shiftMs !== 0 ? new Date(pickerDate.getTime() + shiftMs) : pickerDate
}

/** Place wall-clock time on a calendar day using the offset valid for that day (DST-safe). */
export function setWallClockOnDate(calendarDate: Date, hours: number, minutes: number, offsetMinutes: number): Date {
  const y = calendarDate.getFullYear()
  const mo = calendarDate.getMonth() + 1
  const d = calendarDate.getDate()
  const sign = offsetMinutes >= 0 ? '+' : '-'
  const abs = Math.abs(offsetMinutes)
  const oh = Math.floor(abs / 60)
  const om = abs % 60
  const offsetStr = `${sign}${pad(oh)}:${pad(om)}`
  return new Date(`${y}-${pad(mo)}-${pad(d)}T${pad(hours)}:${pad(minutes)}:00${offsetStr}`)
}

function resolveOffsetMinutesForDate(date: Date, displayOffsetMinutes?: number): number {
  return displayOffsetMinutes !== undefined ? displayOffsetMinutes : -date.getTimezoneOffset()
}

// Get timezone offset in ISO format (+hh:mm or -hh:mm)
function getTimezoneOffset(date: Date, offsetMinutes?: number): string {
  const tzOffset = offsetMinutes !== undefined ? offsetMinutes : -date.getTimezoneOffset()
  const diff = tzOffset >= 0 ? '+' : '-'
  return diff + pad(tzOffset / 60) + ':' + pad(tzOffset % 60)
}

// Convert Date to local time zone ISO string that contains timezone information
export function toISOStringWithTimezone(date: Date, offsetMinutes?: number): string {
  return (
    date.getFullYear() +
    '-' +
    pad(date.getMonth() + 1) +
    '-' +
    pad(date.getDate()) +
    'T' +
    pad(date.getHours()) +
    ':' +
    pad(date.getMinutes()) +
    ':' +
    pad(date.getSeconds()) +
    getTimezoneOffset(date, offsetMinutes)
  )
}

// Convert Date to local time zone ISO string YYYY-MM-DD
export function toISOStringYYYYMMDD(date: Date): string {
  return '' + date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate()) + ''
}

export const getTaskId = (eventId: string): number => {
  const [, rawId] = eventId.split('_')
  return Number(rawId)
}

export const getTaskPrefix = (eventId: string): string => {
  const [prefix] = eventId.split('_')
  return prefix
}

export const getLoadingStatus = (status?: string): string => {
  if (!status) return ''
  return status === 'new' || status === 'scenario_error'
    ? 'Not Loaded'
    : status === 'scenario_done'
    ? 'Loaded'
    : 'Loading'
}
